import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TriageAgentDebugDialog} from '@github-ui/issue-triage-agent/TriageAgentDebugDialog'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  ArrowRightIcon,
  BeakerIcon,
  CommentDiscussionIcon,
  CopyIcon,
  HeartIcon,
  LockIcon,
  PinIcon,
  PinSlashIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, CounterLabel, Heading} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useRef, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {OptionConfig} from '../../components/OptionConfig'
import {BUTTON_LABELS} from '../../constants/buttons'
import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {commitPinIssueMutation} from '../../mutations/pin-issue'
import {commitUnpinIssueMutation} from '../../mutations/unpin-issue'
import type {IssueSidebarPrimaryQuery$data} from '../__generated__/IssueSidebarPrimaryQuery.graphql'
import {ConvertToDiscussionDialog} from '../ConvertToDiscussionDialog'
import {IssueConversationLock} from '../header/IssueConversationLock'
import {IssueDeletionConfirmationDialog} from '../header/IssueDeletionConfirmationDialog'
import {IssueTransferDialog} from '../header/IssueTransferDialog'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import {IssueFeedbackDialog, type IssueFeedbackDialogRef} from '../IssueFeedbackDialog'
import type {OptionsSectionFragment$key} from './__generated__/OptionsSectionFragment.graphql'
import styles from './OptionsSection.module.css'

const DuplicateIssueDialog = React.lazy(async () => {
  const m = await import('../DuplicateIssueDialog')
  return {default: m.DuplicateIssueDialog}
})

const PINNED_ISSUES_LIMIT = 3

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const OptionsSectionFragment = graphql`
  fragment OptionsSectionFragment on Issue {
    id
    number
    title
    body
    isPinned
    locked
    viewerCanDelete
    viewerCanTransfer
    viewerCanConvertToDiscussion
    viewerCanLock
    repository {
      id
      name
      owner {
        login
      }
      visibility
      pinnedIssues(first: 3) {
        totalCount
      }
      viewerCanPinIssues
      isArchived
      viewerIssueCreationPermissions {
        triageable
      }
    }
  }
`

export type OptionsSectionSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

export type OptionsSectionProps = {
  optionsSectionSecondary?: OptionsSectionSecondaryFragment | null
  optionConfig: OptionConfig
  issue: IssueSidebarPrimaryQuery$data
}

export function OptionsSection({issue, optionsSectionSecondary, optionConfig}: OptionsSectionProps) {
  const [showIssueTransferDialog, setShowIssueTransferDialog] = useState(false)
  const [showConvertToDiscussionDialog, setShowConvertToDiscussionDialog] = useState(false)
  const [showIssueDeletionConfirmationDialog, setShowIssueDeletionConfirmationDialog] = useState(false)
  const [showConversationLockDialog, setShowConversationLockDialog] = useState(false)
  const [showDuplicateIssueDialog, setShowDuplicateIssueDialog] = useState(false)
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [showTriageDebugDialog, setShowTriageDebugDialog] = useState(false)
  const feedbackDialogRef = useRef<IssueFeedbackDialogRef>(null)

  const showFeedback = isFeatureEnabled('issues_react_ui_feedback')
  const showTriageDebug = isFeatureEnabled('issues_triage_agent_debug')

  const {
    id,
    number: issueNumber,
    title,
    body,
    viewerCanLock,
    viewerCanTransfer,
    viewerCanDelete,
    viewerCanConvertToDiscussion,
    isPinned,
    locked,
    repository,
  } = useFragment<OptionsSectionFragment$key>(OptionsSectionFragment, issue)

  // If the secondary data is not available, we default to false
  const isTransferInProgress = optionsSectionSecondary?.isTransferInProgress ?? false

  const viewerCanPin = repository.viewerCanPinIssues

  const pinnedIssuesCount = repository?.pinnedIssues?.totalCount || 0

  const canPinNewIssue = pinnedIssuesCount < 3

  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const pinUnpin = useCallback(() => {
    if (!viewerCanPin) return

    if (isPinned) {
      commitUnpinIssueMutation({
        environment,
        input: {issueId: id},
        repositoryId: repository.id,
        currentPinnedCount: pinnedIssuesCount,
        onCompleted: () => {},
        onError: () => {},
      })
    } else {
      if (canPinNewIssue) {
        commitPinIssueMutation({
          environment,
          input: {issueId: id},
          repositoryId: repository.id,
          currentPinnedCount: pinnedIssuesCount,
          onCompleted: () => {},
          onError: () => {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotUnpinIssue,
            })
          },
        })
      }
    }
  }, [addToast, canPinNewIssue, environment, id, isPinned, pinnedIssuesCount, repository.id, viewerCanPin])

  const disablePinningAction = !canPinNewIssue && !isPinned

  const allowDuplicateIssue =
    !repository.isArchived &&
    // Duplication is only shown to users with triage permission in the current repository
    repository.viewerIssueCreationPermissions.triageable

  return (
    <>
      <Heading as="h2" className="sr-only">
        {LABELS.optionsTitle}
      </Heading>
      {/* Note to maintainers: If adding support for new permissions, make sure `hideDivider` in the ParticipantsSection is updated too */}
      <ActionList variant="full" className={styles.ActionListOverrides}>
        {viewerCanTransfer && (
          <ActionList.Item onSelect={() => setShowIssueTransferDialog(true)}>
            <ActionList.LeadingVisual>
              <ArrowRightIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.transferIssue}
          </ActionList.Item>
        )}

        {allowDuplicateIssue && (
          <ActionList.Item onSelect={() => setShowDuplicateIssueDialog(true)}>
            <ActionList.LeadingVisual>
              <CopyIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.duplicateIssue}
          </ActionList.Item>
        )}

        {viewerCanLock && (
          <ActionList.Item onSelect={() => setShowConversationLockDialog(true)}>
            <ActionList.LeadingVisual>
              <LockIcon />
            </ActionList.LeadingVisual>
            <span>{locked ? LABELS.lock.buttonConfirmUnlock : LABELS.lock.buttonConfirmLock}</span>
          </ActionList.Item>
        )}

        {viewerCanPin && (
          <ActionList.Item
            aria-disabled={disablePinningAction || undefined}
            onSelect={disablePinningAction ? undefined : pinUnpin}
            className={clsx(disablePinningAction && styles.DisablePin)}
            inactiveText={
              disablePinningAction
                ? `${PINNED_ISSUES_LIMIT}/${PINNED_ISSUES_LIMIT} issues already pinned. Unpin an issue to pin this one.`
                : undefined
            }
          >
            <ActionList.LeadingVisual>
              {isPinned ? (
                <PinSlashIcon className={clsx(disablePinningAction && styles.DisablePin)} />
              ) : (
                <PinIcon className={clsx(disablePinningAction && styles.DisablePin)} />
              )}
            </ActionList.LeadingVisual>
            <span>{isPinned ? BUTTON_LABELS.unpinIssue : BUTTON_LABELS.pinIssue}</span>
            {pinnedIssuesCount > 0 && (
              <ActionList.TrailingVisual>
                <CounterLabel>
                  {pinnedIssuesCount}/{PINNED_ISSUES_LIMIT}
                </CounterLabel>
              </ActionList.TrailingVisual>
            )}
          </ActionList.Item>
        )}
        {viewerCanConvertToDiscussion && (
          <ActionList.Item onSelect={() => setShowConvertToDiscussionDialog(true)}>
            <ActionList.LeadingVisual>
              <CommentDiscussionIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.convertToDiscussion}
          </ActionList.Item>
        )}
        {showTriageDebug && (
          <ActionList.Item onSelect={() => setShowTriageDebugDialog(true)}>
            <ActionList.LeadingVisual>
              <BeakerIcon />
            </ActionList.LeadingVisual>
            Debug triage
          </ActionList.Item>
        )}
        {viewerCanDelete && (
          <ActionList.Item variant="danger" onSelect={() => setShowIssueDeletionConfirmationDialog(true)}>
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.deleteIssue}
          </ActionList.Item>
        )}
        {showFeedback && (
          <ActionList.Item
            onSelect={() => {
              if (!showFeedbackDialog) {
                setShowFeedbackDialog(true)
                feedbackDialogRef.current?.openDialog()
              }
            }}
          >
            <ActionList.LeadingVisual>
              <HeartIcon />
            </ActionList.LeadingVisual>
            Give feedback
            <IssueFeedbackDialog ref={feedbackDialogRef} onClose={() => setShowFeedbackDialog(false)} />
          </ActionList.Item>
        )}
      </ActionList>
      {showIssueTransferDialog && (
        <IssueTransferDialog
          owner={repository.owner.login}
          repository={repository.name}
          currentRepoVisibility={repository.visibility}
          issueId={id}
          isTransferInProgress={isTransferInProgress}
          onClose={() => setShowIssueTransferDialog(false)}
        />
      )}
      {showConvertToDiscussionDialog && (
        <ConvertToDiscussionDialog
          owner={repository.owner.login}
          repository={repository.name}
          issueId={id}
          onClose={() => setShowConvertToDiscussionDialog(false)}
        />
      )}
      {showConversationLockDialog && (
        <IssueConversationLock issueId={id} isUnlocked={!locked} onClose={() => setShowConversationLockDialog(false)} />
      )}
      {showIssueDeletionConfirmationDialog && (
        <IssueDeletionConfirmationDialog
          issueId={id}
          onSuccessfulDeletion={optionConfig.onIssueDelete}
          afterDelete={optionConfig.navigateBack}
          onClose={() => setShowIssueDeletionConfirmationDialog(false)}
        />
      )}
      {showDuplicateIssueDialog && (
        <Suspense fallback={null}>
          <DuplicateIssueDialog
            issue={issue}
            showDuplicateIssueDialog={showDuplicateIssueDialog}
            setShowDuplicateIssueDialog={setShowDuplicateIssueDialog}
            optionConfig={optionConfig}
            repositoryOwner={repository.owner.login}
            repositoryName={repository.name}
            title={title}
            body={body}
          />
        </Suspense>
      )}
      {showTriageDebugDialog && (
        <TriageAgentDebugDialog
          repoOwner={repository.owner.login}
          repoName={repository.name}
          issueNumber={issueNumber}
          isOpen={showTriageDebugDialog}
          onClose={() => setShowTriageDebugDialog(false)}
        />
      )}
    </>
  )
}
